import {
    FormComponentValue,
    FormControlProps,
    FormElementControlPropsType,
    FormStaticControlPropsType,
    getFromComponentPropsClass,
} from './control';
import { Controller as ReactFormController, useFormState } from 'react-hook-form';
import buildFormControlProperties from 'components/common/form/buildFormControlProperties';
import React, { Fragment, Suspense } from 'react';
import { FormDataAPIType, FormElementProps, RawFormComponentType, RawFormElementProps } from 'components/common/form';
import Components, { ComponentKey } from 'components/common/ComponentIndex';
import { AssocArray } from 'tools/types';
import { prepareFormElementProps } from 'components/common/form/formTools';
import { Box } from '@mui/material';
import { FormComponentDependency } from 'components/common/form/dependency';
import useBundleTranslation from 'i18n';
import { FormElementWrapper } from 'components/common/form/element/FormElementWrapper';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import FormElementErrorMessage from 'components/common/form/element/FormElementErrorMessage';
import useFormDependency from 'components/common/form/element/useFormDependency';

interface TemplateFormComponentProps {
    component: ComponentKey;
    componentValues?: Array<FormComponentValue>;
    elementProps: RawFormElementProps | FormDataAPIType;
    name: string;
    props?: AssocArray<any>;
    deps?: Array<FormComponentDependency>;
    wrapper?: boolean;
    designVariant?: string;
    label?: string;
    childComponentsRender?: (...args: any[]) => React.JSX.Element;
    components?: Array<RawFormComponentType>;
}

export function TemplateFormComponent({
    component,
    componentValues,
    elementProps,
    name,
    props = {},
    deps = [],
    wrapper = false,
    label,
    childComponentsRender,
    components,
    designVariant,
}: TemplateFormComponentProps) {
    const formElementProps = prepareFormElementProps({
        ...elementProps,
        component: {
            uid: name,
            name: name,
            label: label ?? name,
            component: component,
            components: components ?? [],
            designVariant: designVariant,
            props: props,
            deps: deps,
        },
    });

    const templateProps = {
        elementProps: formElementProps,
        values: componentValues ?? [],
        wrapper: wrapper,
        childComponentsRender: childComponentsRender,
    };

    return elementProps.form ? <FormTemplateFormComponent {...templateProps} /> : <Template {...templateProps} />;
}

interface ITemplate {
    elementProps: FormElementProps;
    values: Array<FormComponentValue>;
    wrapper?: boolean;
    childComponentsRender?: (...args: any[]) => React.JSX.Element;
}

function FormTemplateFormComponent({ elementProps, values, wrapper, childComponentsRender }: ITemplate) {
    const { componentProps, componentValues } = useFormDependency(elementProps, values);
    const fullElementProps = { ...elementProps, componentProps: componentProps };

    const classList = getFromComponentPropsClass(fullElementProps, componentValues);
    // Wrapper Components
    if (fullElementProps.component.fieldType == 'wrapper' && childComponentsRender) {
        return (
            <span className={classList}>
                {fullElementProps.component.components.map((c, i) => {
                    return <Fragment key={i}>{childComponentsRender(elementProps, c)}</Fragment>;
                })}
            </span>
        );
    }

    return <Template elementProps={fullElementProps} values={componentValues} wrapper={wrapper} />;
}

function Template({ elementProps, values, wrapper }: ITemplate) {
    const { t } = useBundleTranslation();
    const { errors } = useFormState({ control: elementProps?.form?.hookFormControl });
    let controller = (
        <>
            <ReactHookFormController elementProps={elementProps} componentValues={values} />
            <FormElementErrorMessage elementProps={elementProps} errors={errors} t={t} />
        </>
    );

    // Static Components
    if (elementProps.component.fieldType == 'static') {
        controller = React.createElement(
            Components[elementProps.component.component] as React.FC<FormStaticControlPropsType>,
            { elementProps: elementProps },
        );
    }

    return wrapper ? (
        <FormElementWrapper elementProps={elementProps} componentValues={values}>
            {controller}
        </FormElementWrapper>
    ) : (
        <Box>{controller}</Box>
    );
}

export default function ReactHookFormController({
    componentValues,
    elementProps,
    afterChange,
}: {
    componentValues?: Array<FormComponentValue>;
    elementProps: FormElementProps;
    afterChange?: (event: any) => void;
}) {
    return (
        <Suspense>
            <ReactFormController
                name={elementProps.component.name}
                defaultValue={elementProps.data[elementProps.component.name]}
                control={elementProps.form.hookFormControl}
                render={({ field }) => {
                    let component = <div>ERROR</div>;
                    const controlData = buildFormControlProperties(elementProps, field, componentValues, afterChange);
                    if (controlData.isValid) {
                        component = React.createElement(
                            controlData.componentName as React.FC<FormElementControlPropsType<FormControlProps>>,
                            {
                                controlProps: controlData.controlProps,
                                elementProps: elementProps,
                            },
                        );
                    }
                    return component;
                }}
            />
        </Suspense>
    );
}
